import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Modal } from 'antd';
import './plan-limits-explainer.css';

/**
 * Presentation-style walkthrough of the plan-limit consumption system: what's
 * shipped today (definitions, enforcement, PA-time checks, live visibility)
 * and what's proposed next (reservation ledger, eligibility, cross-person
 * draws — see docs/BACKEND_REQUIREMENTS_*.md). Content lives here, inline,
 * rather than as markdown, so the "LIVE" vs "PLANNED" framing stays a single
 * source of truth with the rest of the admin UI.
 */

const SLIDES = [
  {
    tone: 'problem',
    badge: '⚠ Why this exists',
    headline: <>Nobody should exceed their limit <em>without us knowing</em>.</>,
    body: 'Every plan promises a cap — ₦500,000 inpatient, two antenatal visits, ten bed nights a year. Today that promise is only checked when a claim is filed, sometimes weeks after care was given. By then the money is already spent.',
    visual: (
      <div className="ple-flow">
        <div className="ple-flow__step">
          <span className="ple-flow__step-label">Care given</span>
          <span className="ple-flow__step-value">Day 0</span>
        </div>
        <span className="ple-flow__arrow">→</span>
        <div className="ple-flow__step">
          <span className="ple-flow__step-label">PA filed</span>
          <span className="ple-flow__step-value">Day 0–2</span>
        </div>
        <span className="ple-flow__arrow">→</span>
        <div className="ple-flow__step ple-flow__step--warn">
          <span className="ple-flow__step-label">Claim posts</span>
          <span className="ple-flow__step-value">Weeks later</span>
        </div>
        <span className="ple-flow__arrow">→</span>
        <div className="ple-flow__step ple-flow__step--warn">
          <span className="ple-flow__step-label">Breach found</span>
          <span className="ple-flow__step-value">Too late</span>
        </div>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '✓ Live now · 1 of 4',
    headline: <>Every cap starts as <em>one row</em>.</>,
    body: 'An admin defines a limit with a metric type (amount, visit count, bed nights…), a scope (per enrollee or per policy), a target (the whole plan, a category, or a single item), and a period.',
    visual: (
      <div className="ple-def-card">
        <div className="ple-def-cell">
          <span className="ple-def-cell__label">Metric</span>
          <span className="ple-def-cell__value ple-def-cell__value--accent">Amount</span>
        </div>
        <div className="ple-def-cell">
          <span className="ple-def-cell__label">Value</span>
          <span className="ple-def-cell__value">₦500,000</span>
        </div>
        <div className="ple-def-cell">
          <span className="ple-def-cell__label">Scope</span>
          <span className="ple-def-cell__value">Enrollee</span>
        </div>
        <div className="ple-def-cell">
          <span className="ple-def-cell__label">Target</span>
          <span className="ple-def-cell__value">Plan-wide</span>
        </div>
        <div className="ple-def-cell">
          <span className="ple-def-cell__label">Period</span>
          <span className="ple-def-cell__value">Annual</span>
        </div>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '✓ Live now · 2 of 4',
    headline: <>Choose how hard the system <em>pushes back</em>.</>,
    body: 'Enforcement is set per plan. Disabled just shows the numbers. Advisory warns the reviewer and asks for confirmation before letting an over-limit request through. Strict blocks it outright at the API.',
    visual: (
      <div className="ple-chip-row">
        <span className="ple-chip"><span className="ple-chip__dot" style={{ background: '#8891a3' }} />Disabled</span>
        <span className="ple-chip ple-chip--on"><span className="ple-chip__dot" style={{ background: 'var(--accent)' }} />Advisory (default)</span>
        <span className="ple-chip"><span className="ple-chip__dot" style={{ background: '#ff6b5e' }} />Strict</span>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '✓ Live now · 3 of 4',
    headline: <>Checked the moment it matters — <em>not weeks later</em>.</>,
    body: 'Every PA submission is checked against real consumption in real time. Advisory mode returns the exact numbers — limit, consumed, proposed, excess — and asks for confirmation. Strict mode blocks the request before it ever reaches approval.',
    visual: (
      <div className="ple-flow">
        <div className="ple-flow__step">
          <span className="ple-flow__step-label">PA submitted</span>
          <span className="ple-flow__step-value">₦60,000</span>
        </div>
        <span className="ple-flow__arrow">→</span>
        <div className="ple-flow__step ple-flow__step--hit">
          <span className="ple-flow__step-label">Checked live</span>
          <span className="ple-flow__step-value">vs. ₦500,000 cap</span>
        </div>
        <span className="ple-flow__arrow">→</span>
        <div className="ple-flow__step ple-flow__step--hit">
          <span className="ple-flow__step-label">Decision</span>
          <span className="ple-flow__step-value">Warn or block</span>
        </div>
      </div>
    ),
  },
  {
    tone: 'live',
    badge: '✓ Live now · 4 of 4',
    headline: <>Every enrollee's usage, <em>visible in one place</em>.</>,
    body: "The consumption view shows exactly how much of each limit is used — a metric badge, a plain-English calculation rule on hover, and a progress bar per limit — so nothing is a surprise at reconciliation time.",
    visual: (
      <div className="ple-progress-mock">
        <div className="ple-progress-mock__head">
          <span><span className="ple-progress-mock__tag">Monetary</span>Amount · Plan-wide · Annual</span>
        </div>
        <div className="ple-progress-mock__bar">
          <div className="ple-progress-mock__bar-fill" />
        </div>
        <div className="ple-progress-mock__foot">
          <span>₦310,000 of ₦500,000</span>
          <span>62% used</span>
        </div>
      </div>
    ),
  },
  {
    tone: 'planned',
    badge: '◐ Planned · closing the gap',
    headline: <>An approval should reserve the money <em>the instant it's approved</em>.</>,
    body: 'Two PAs against the same ₦100,000 pool can each pass their own filing-time check today — approved-but-unclaimed amounts aren’t counted yet. Both get approved, and the pool is over-committed once claims post. A reservation ledger holds the amount from the moment of approval, closing the gap.',
    visual: (
      <div className="ple-pool">
        <div className="ple-pool__bar">
          <div className="ple-pool__clip">
            <div className="ple-pool__seg ple-pool__seg--a" style={{ left: '0%', width: '50%' }} />
            <div className="ple-pool__seg ple-pool__seg--b" style={{ left: '50%', width: '50%' }} />
          </div>
          <div className="ple-pool__cap" style={{ left: '83.3%' }}>
            <span className="ple-pool__cap-label">Cap ₦100,000</span>
          </div>
        </div>
        <div className="ple-pool__legend">
          <span><span className="ple-pool__legend-dot" style={{ background: '#a78bfa' }} />PA-A ₦60,000 (approved)</span>
          <span><span className="ple-pool__legend-dot" style={{ background: '#ff6b5e' }} />PA-B ₦60,000 (over cap)</span>
        </div>
      </div>
    ),
  },
  {
    tone: 'planned',
    badge: '◐ Planned · who it applies to',
    headline: <>Limits will know <em>who they're for</em>.</>,
    body: 'Age, gender, and relationship to the principal member — so a pediatric vaccine cap doesn’t accidentally apply to an adult, and a maternity limit never touches a male dependent. Ineligible enrollees are skipped entirely, not shown as "compliant."',
    visual: (
      <div className="ple-chip-row">
        <span className="ple-chip ple-chip--on">Dependents only</span>
        <span className="ple-chip ple-chip--on">Ages 0–12</span>
        <span className="ple-chip ple-chip--on">Any gender</span>
      </div>
    ),
  },
  {
    tone: 'planned',
    badge: '◐ Planned · whose pool it draws from',
    headline: <>Some care belongs to <em>someone else's pool</em>.</>,
    body: 'A newborn’s neonatal care should draw against the mother’s maternity limit — not open a fresh pool for a baby with no history of its own. Cross-person draws let a limit resolve against a different family member’s bucket.',
    visual: (
      <div className="ple-flow">
        <div className="ple-flow__step">
          <span className="ple-flow__step-label">Patient</span>
          <span className="ple-flow__step-value">Newborn</span>
        </div>
        <span className="ple-flow__arrow">→</span>
        <div className="ple-flow__step ple-flow__step--hit">
          <span className="ple-flow__step-label">Draws from</span>
          <span className="ple-flow__step-value">Mother's pool</span>
        </div>
      </div>
    ),
  },
  {
    tone: 'closing',
    badge: '★ The picture, put together',
    headline: <>By design — <em>not by luck</em>.</>,
    body: "That's the whole system, start to finish. Live steps already run in production; planned steps are written up for backend as concrete proposals, not vague ideas.",
    visual: (
      <div className="ple-recap">
        <div className="ple-recap__row"><span className="ple-recap__num">01</span>Define the limit — metric, scope, target, period</div>
        <div className="ple-recap__row"><span className="ple-recap__num">02</span>Set enforcement — disabled, advisory, or strict</div>
        <div className="ple-recap__row"><span className="ple-recap__num">03</span>Check it live — at the moment a PA is filed</div>
        <div className="ple-recap__row"><span className="ple-recap__num">04</span>See it live — consumption view, badges, tooltips</div>
        <div className="ple-recap__row ple-recap__row--planned"><span className="ple-recap__num">05</span>Reserve on approval — close the double-approval race</div>
        <div className="ple-recap__row ple-recap__row--planned"><span className="ple-recap__num">06</span>Apply to the right people — eligibility rules</div>
        <div className="ple-recap__row ple-recap__row--planned"><span className="ple-recap__num">07</span>Draw from the right pool — cross-person draws</div>
      </div>
    ),
  },
];

export const PlanLimitsExplainerModal = ({ open, onClose }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const goTo = useCallback((next) => {
    setStep((current) => {
      const clamped = Math.max(0, Math.min(SLIDES.length - 1, next));
      return clamped === current ? current : clamped;
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') goTo(step + 1);
      else if (e.key === 'ArrowLeft') goTo(step - 1);
      else if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, step, goTo, onClose]);

  const slide = useMemo(() => SLIDES[step], [step]);
  const isFirst = step === 0;
  const isLast = step === SLIDES.length - 1;

  return (
    <Modal
      className="ple-modal"
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      destroyOnClose
      maskClosable
      centered
    >
      <div className="ple-stage" data-tone={slide.tone}>
        <div className="ple-glow" aria-hidden />

        <div className="ple-topbar">
          <span className="ple-brand">
            <span className="ple-brand__dot" aria-hidden />
            Plan limits, explained
          </span>
          <button type="button" className="ple-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="ple-body">
          <div className="ple-slide" key={step}>
            <span className="ple-badge">
              <span className="ple-badge__icon" aria-hidden>{slide.badge.slice(0, 1)}</span>
              {slide.badge.slice(2)}
            </span>
            <h2 className="ple-headline">{slide.headline}</h2>
            <p className="ple-body-text">{slide.body}</p>
            <div className="ple-visual">{slide.visual}</div>
          </div>
        </div>

        <div className="ple-nav">
          <div className="ple-dots" role="tablist" aria-label="Slides">
            {SLIDES.map((s, i) => (
              <button
                key={String(i)}
                type="button"
                role="tab"
                aria-selected={i === step}
                aria-label={`Go to slide ${i + 1}`}
                className={`ple-dot${i === step ? ' ple-dot--active' : ''}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
          <div className="ple-nav__buttons">
            <span className="ple-step-count">{String(step + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}</span>
            <button type="button" className="ple-btn" onClick={() => goTo(step - 1)} disabled={isFirst}>
              Back
            </button>
            {isLast ? (
              <button type="button" className="ple-btn ple-btn--primary" onClick={onClose}>
                Done
              </button>
            ) : (
              <button type="button" className="ple-btn ple-btn--primary" onClick={() => goTo(step + 1)}>
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PlanLimitsExplainerModal;
